import Provider, { ToolbarContext, ToolbarContextProvider } from './Provider'
import React, { useState, useEffect, useContext, MouseEvent } from 'react'
import useStyles from './Styles'
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@material-ui/core'
import { ShareButton, ConfigButton, PlaylistButton } from 'components'
import { ValidationButton } from 'smart-components/validation-button'
import { IProps } from './Interface'
import InitiativesSelectorDialogService from 'smart-components/initiatives-selector-dialog/Service'
import { InitiativeSelectorDialogState } from 'smart-components/initiatives-selector-dialog/store/State'
import { useNotification } from 'helpers'
import cx from 'classnames'

const Toolbar: React.FC<IProps> = ({ publicMode }) => {
  const { state, handlers } = useContext<ToolbarContext>(ToolbarContextProvider)
  const { emitSuccess } = useNotification()
  const [initiativesSelectorState, setInitiativesSelectorState] = useState<InitiativeSelectorDialogState>(
    InitiativesSelectorDialogService.state
  )

  const [anchors, setAnchors] = useState({ gear: null, share: null })

  const handleClick = (name: string) => (event: MouseEvent<HTMLElement>) => {
    setAnchors({ ...anchors, [name]: event.currentTarget })
  }

  const handlePopoverClose = (name: string) => () => {
    setAnchors({ ...anchors, [name]: null })
  }

  const classes = useStyles({ open: !!anchors.share, publicMode })

  useEffect(() => {
    InitiativesSelectorDialogService.addListener(
      InitiativesSelectorDialogService.Events.onStateChange,
      setInitiativesSelectorState
    )
    return () => {
      InitiativesSelectorDialogService.removeListener(
        InitiativesSelectorDialogService.Events.onStateChange,
        setInitiativesSelectorState
      )
    }
  }, [])

  const markup = handlers.getSocialMediaButtonMarkup()

  return (
    <React.Fragment>
      <Box className={classes.root}>
        <ShareButton
          className={cx(classes.shareButton, 'share-button')}
          anchor={anchors.share}
          disabled={!state.isMiniProfileComplete}
          showPdfOption={!publicMode || state.isComplete}
          pdfDisabled={!state.isComplete}
          pdfDownloading={state.downloadingPDF}
          onClick={handleClick('share')}
          onPopoverClose={handlePopoverClose('share')}
          onDownloadPDF={handlers.downloadPDF}
          onDownloadPDFHelper={handlers.showValidationErrors}
          onCopyProfile={handlers.copyToClipboard}
          onGetSocialMediaButton={handlers.getPointrSocialMediaButton}
        />
        {!publicMode && (
          <ConfigButton
            className={cx(classes.configButton, 'configuration-button')}
            anchor={anchors.gear}
            onClick={handleClick('gear')}
            onPopoverClose={handlePopoverClose('gear')}
            onLogout={handlers.onLogOff}
            onClickSettings={handlers.onClickSettings}
            hasPendingInitiatives={initiativesSelectorState.initiativesOptions.length > 0}
            onOpenPendingInitiatives={() => InitiativesSelectorDialogService.setIsDialogOpen('OPEN')}
          />
        )}
        <PlaylistButton visible={state.displayPlaylists && !publicMode} />
        {!publicMode && <ValidationButton />}
      </Box>
      <Dialog
        open={state.isSocialMediaButtonDialogOpen}
        onClose={handlers.getPointrSocialMediaButton}
        fullWidth
        maxWidth="md"
        aria-labelledby="social-media-button-dialog-title"
      >
        <DialogTitle id="social-media-button-dialog-title" style={{ color: 'rgb(52, 27, 108)' }}>
          Get your Pointr social media button
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="toolbar-copy-and-paste-description-label">
            Copy and paste the following markup or click on the "Copy to my clipboard" button, to get you Pointr social
            media button.
          </DialogContentText>
          <div className={classes.markup}>
            <code id="toolbar-markup-label">{markup}</code>
          </div>
          <DialogContentText className={classes.displaySocialMediaButtonText} id="toolbar-markup-description-label">
            This markup displays a clickable link like this: &nbsp;
            <a
              id="toolbar-public-profile-label"
              rel="noopener noreferrer"
              className={classes.displaySocialMediaButtonLink}
              href={handlers.getPubicProfileUrl()}
              target="_blank"
            >
              <img src="/icons/pointr-mini.png" alt="Pointr digital profile" />
            </a>
          </DialogContentText>
          <DialogContentText id="toolbar-pointr-social-media-link">
            * Obs: In case the place where you want to use this button does not allow plain HTML, you can download the
            pointr social media button by clicking{' '}
            <a id="toolbar-download-link" href="/icons/pointr-mini.png" download>
              here
            </a>{' '}
            and use your public profile URL: {handlers.getPubicProfileUrl()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            id="toolbar-copy-to-my-clipboard-button"
            onClick={() => {
              if (handlers.onCopySocialButtonMarkup(markup)) {
                emitSuccess('Social media button markup copied.')
              }
            }}
            color="primary"
          >
            Copy to my clipboard
          </Button>
          <Button id="toolbar-close-button" onClick={handlers.getPointrSocialMediaButton} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

export default (props: IProps) => (
  <Provider {...props}>
    <Toolbar {...props} />
  </Provider>
)
